/**
 * LIFE EMPIRE - client entry point.
 *
 * Owns the render loop, input, and the bridge to the simulation. All game rules
 * live behind `GameFacade`; nothing in this file may reach into simulation
 * internals (ADR-0002).
 */
import * as THREE from 'three';
import { GameFacade } from '../sim/facade/GameFacade.js';
import { buildNeighbourhood } from './world/Neighbourhood.js';
import { createSky, followShadowCamera } from './world/Sky.js';
import { createCrowd } from './world/Crowd.js';
import { createSignposts } from './world/Signposts.js';
import { createCustomers, type CustomerHost } from './world/Customers.js';
import { createRain } from './world/Rain.js';
import { createPipeline, type RenderStats } from './world/Postprocess.js';
import { setGroundWetness } from './world/Materials.js';
import { YardWorkView } from './world/YardWork.js';
import { createVehicles } from './world/Vehicles.js';
import { createVehicleController } from './player/VehicleController.js';
import { createTraffic, type TrafficSystem } from './world/Traffic.js';
import {
  collectDiagnostics, stage, finishBoot, showFatalError, shouldSkipPostProcessing,
  registerServiceWorker,
} from './Boot.js';
import { createInteriors, type InteriorDef } from './world/Interiors.js';
import type { CarState } from './world/CarDynamics.js';
import { createPlayer } from './player/Player.js';
import { CameraRig } from './player/CameraRig.js';
import { findNearestLocation } from './Interaction.js';
import { getLocation } from '../sim/data/world.js';
import { createUi } from './ui/Ui.js';
import { BrowserStorage } from './BrowserStorage.js';
import { createAudio } from './audio/Audio.js';
import { createTouchControls, isTouchPrimary, type TouchControls } from './TouchControls.js';
import {
  createTitleScreen, describeSave, DEFAULT_SETTINGS, type GameSettings,
} from './ui/TitleScreen.js';

export interface GameHandle {
  facade: GameFacade;
  renderer: THREE.WebGLRenderer;
  /** Exposed so smoke tests and screenshot capture can frame a shot. */
  cameraRig: CameraRig;
  /** Exposed for diagnostics only. */
  scene: THREE.Scene;
  /**
   * Per-frame cost, split into the world and the whole composited frame.
   * `renderer.info` alone cannot answer this once a composer is in play.
   */
  renderStats: RenderStats;
  /**
   * Whether post-processing is running. A phone should report false: the
   * stack is skipped there deliberately, and a check that cannot see the
   * difference cannot catch it coming back.
   */
  composited(): boolean;
  /** Ambient traffic, exposed so the performance report can count it. */
  traffic: TrafficSystem;
  /** Walkable rooms, for tests and capture. */
  interior: {
    readonly current: string | null;
    readonly hotspot: string | null;
    enter(locationId: string): void;
    leave(): void;
    goTo(kind: string): void;
  };
  three: typeof THREE;
  /**
   * Place the avatar directly. A test hook: mowing a whole lawn through the
   * movement keys is not something a smoke test can steer reliably.
   */
  moveAvatarTo(x: number, z: number): void;
  /**
   * The drivable vehicle, for tests and capture.
   *
   * `toggle` runs the same enter/exit path the F key does, so a test that
   * proves driving works proves the thing the player actually uses rather
   * than a parallel route into the same state.
   */
  driving: {
    readonly active: boolean;
    readonly state: CarState;
    toggle(): void;
    place(x: number, z: number, heading: number): void;
  };
  /** Simulated key state, so tests can drive movement without a real keyboard. */
  press(code: string): void;
  release(code: string): void;
  /**
   * Stop or resume the animation loop. Screenshot capture pauses it: on a
   * software-GL runner a continuously redrawing 3D scene starves the capture,
   * and Playwright times out waiting for a frame it can never get.
   */
  setRunning(running: boolean): void;
  /**
   * Advance the game by a fixed step. Used by tests to drive it
   * deterministically. Pass `render: false` for bulk simulation - drawing every
   * frame of a multi-hour in-game playthrough dominates the runtime on a
   * software-GL CI runner and proves nothing extra.
   */
  step(seconds: number, render?: boolean): void;
  /** Skip the title screen. Used by smoke tests and screenshot capture. */
  startGame(): void;
  /** Dismiss the pause menu and any transition fade. */
  closeMenus(): void;
  /** Back to the title screen. */
  showTitle(): void;
  ready: boolean;
}

declare global {
  interface Window {
    __LIFE_EMPIRE__?: GameHandle;
  }
}

/** Injected at build time from package.json, so the title never lies. */
const APP_VERSION: string = __APP_VERSION__;

/** The desktop build gets a Quit item; the web build must not. */
function isDesktop(): boolean {
  return typeof navigator !== 'undefined' && /Electron/i.test(navigator.userAgent);
}

async function boot(): Promise<void> {
  const app = document.getElementById('app');
  if (!app) throw new Error('#app is missing');

  await stage('Loading simulation');
  const facade = new GameFacade(new BrowserStorage());

  // --- renderer ------------------------------------------------------------
  const renderer = new THREE.WebGLRenderer({
    antialias: true,
    powerPreference: 'high-performance',
  });
  // A modern iPhone reports devicePixelRatio 3, so an uncapped buffer is nine
  // times the pixels of a CSS-pixel render - and every one of them goes through
  // the ambient-occlusion pass. Capping at 1.5 on a phone is the single
  // largest thing keeping the mobile build interactive, and at phone viewing
  // distance the difference is not visible.
  const touchPrimary = isTouchPrimary();
  renderer.setPixelRatio(Math.min(window.devicePixelRatio, touchPrimary ? 1.5 : 2));
  renderer.setSize(window.innerWidth, window.innerHeight);
  renderer.shadowMap.enabled = true;
  // Soft, now that the art direction is aiming at realism rather than at a
  // stylised look: a hard shadow edge at every distance is one of the things
  // that reads as synthetic (VISUAL_REALISM_AUDIT R-04). The extra taps are
  // affordable inside the current budget headroom.
  renderer.shadowMap.type = THREE.PCFSoftShadowMap;
  renderer.toneMapping = THREE.ACESFilmicToneMapping;
  renderer.toneMappingExposure = 1.15;
  renderer.outputColorSpace = THREE.SRGBColorSpace;
  app.appendChild(renderer.domElement);
  renderer.domElement.id = 'game-canvas';

  const scene = new THREE.Scene();
  // Aerial perspective (VISUAL_REALISM_AUDIT: distant buildings carried the
  // same contrast and saturation as near ones, which no outdoor photograph
  // does). The old linear fog began at 150 units - past almost everything the
  // camera ever frames - so in practice the scene had no depth cue at all.
  // Exponential-squared starts attenuating immediately and falls off the way
  // real haze does: about 3% at 100 units, 16% at 150, half by 300.
  scene.fog = new THREE.FogExp2(0xb3d0e8, 0.0028);

  const camera = new THREE.PerspectiveCamera(
    58, window.innerWidth / window.innerHeight, 0.1, 900,
  );
  const rig = new CameraRig(camera);

  // Ambient occlusion and colour output. Everything drawn from here on goes
  // through this rather than through renderer.render directly.
  // A phone skips the post-processing stack unless asked for it with ?fx=1.
  // Ambient occlusion is the most demanding thing in the build and the most
  // likely to fail on a mobile driver, and a working plain render beats a
  // beautiful blank screen.
  const pipeline = createPipeline(renderer, scene, camera, {
    disabled: shouldSkipPostProcessing(touchPrimary),
  });

  /**
   * Image-based lighting (VISUAL_REALISM_AUDIT R-02).
   *
   * Without an environment map, metal has nothing to reflect and reads as
   * painted plastic, glass reflects nothing, and no surface picks up the colour
   * of the sky above it. The sky already exists as a shader dome, so the
   * environment is a capture of it rather than anything new to author.
   *
   * Re-captured when the light has actually moved rather than every frame: a
   * PMREM capture is six render passes plus a mip chain, and the sky changes
   * slowly. A tenth of an in-game hour is well below the point where a player
   * could see the environment lagging the sun.
   */
  let lastEnvHour = -99;
  function refreshEnvironment(hour: number): void {
    if (Math.abs(hour - lastEnvHour) < 0.1) return;
    lastEnvHour = hour;
    sky.captureEnvironment(renderer, scene);
  }

  // --- world ---------------------------------------------------------------
  await stage('Building Maple Grove');
  const world = buildNeighbourhood();
  scene.add(world.group);

  const sky = createSky();
  scene.add(sky.group);

  await stage('Putting traffic on the roads');
  const crowd = createCrowd(facade.sim.world);
  scene.add(crowd.group);

  // World-space labels so the neighbourhood itself tells the player where
  // things are, rather than only a bar at the bottom of the screen.
  const signposts = createSignposts();
  scene.add(signposts.group);

  // The customer who booked you is standing outside their house waiting.
  const customers = createCustomers();
  scene.add(customers.group);

  // Weather. The simulation rolls it at each day boundary; this draws it.
  const rain = createRain();
  scene.add(rain.group);
  let shownWeather: string | null = null;
  /** Eased separately from the weather itself, so the ground dries slowly. */
  let wetness = 0;

  // Mowing is something you watch happen, not a bar that fills while you stand
  // still. The avatar walks the yard, the grass is cut behind them, and
  // clippings spray from the deck.
  const yardWork = new YardWorkView(scene as unknown as THREE.Group);
  let yardWorkLocation: string | null = null;
  let lastWorkX = 0;
  let lastWorkZ = 0;

  // The vehicle you own is parked on your own driveway, so you walk past it
  // every morning. Ownership that is only a number on a screen is not a reward.
  const homeLot = getLocation('home');
  const vehicles = createVehicles(
    homeLot.x + homeLot.entryX * 0.55, homeLot.z + homeLot.entryZ - 6, Math.PI / 2,
  );
  scene.add(vehicles.group);
  let shownVehicle: string | null = null;

  // The driveway is where a car sits until it has been moved. A save records
  // where the player actually left it; without one - a fresh purchase, or a
  // save written before ADR-0011 - this is the fallback, which is exactly
  // where a player of the previous build last saw it.
  const DRIVEWAY = {
    x: homeLot.x + homeLot.entryX * 0.55,
    z: homeLot.z + homeLot.entryZ - 6,
    heading: Math.PI / 2,
  };
  // Ambient traffic. Two instanced draws for the whole fleet, so the cost is
  // effectively fixed no matter how many cars are moving.
  const traffic = createTraffic();
  scene.add(traffic.group);

  // Walkable rooms, standing outside the world bounds. Only the one being
  // used is visible, so the rest cost nothing.
  const interiors = createInteriors();
  scene.add(interiors.group);
  let inside: InteriorDef | null = null;
  /** Where the player stood before going in, so leaving puts them back. */
  let outsideReturn = { x: 0, z: 0 };

  const driver = createVehicleController({ group: vehicles.group });
  driver.place(facade.vehicleParked() ?? DRIVEWAY);

  const home = getLocation('home');
  await stage('Preparing your character');
  const player = createPlayer(home.x + home.entryX + 7, home.z + home.entryZ + 3);
  scene.add(player.group);

  // Fast travel and debug teleports originate in the simulation, but the avatar
  // lives here - without this the player would simply not move.
  facade.onTeleport((x, z, outwardX, outwardZ) => {
    player.position.set(x, 0, z);
    rig.faceFrom(outwardX, outwardZ);
  });

  // --- audio ---------------------------------------------------------------
  // Entirely synthesised at runtime: no files, nothing to license. Browsers
  // require a gesture before audio may start, so it is resumed on first input.
  const audio = createAudio();

  // --- input ---------------------------------------------------------------
  const keys = new Set<string>();
  let pointerLocked = false;

  /**
   * The game sits in `title` until the player starts or loads. In that mode the
   * simulation is frozen and the camera flies slowly over the neighbourhood
   * behind the menu, so the first thing anyone sees is the world, not a form.
   */
  let mode: 'title' | 'playing' = 'title';
  let titleTime = 0;

  // A phone gets medium by default: half-resolution ambient occlusion, a 1024
  // shadow map and a thinner crowd. Stored settings still win, so an owner who
  // raises it keeps it - this only changes what an unconfigured phone starts
  // at, which is the only case where the default matters.
  const settings: GameSettings = {
    ...DEFAULT_SETTINGS,
    ...(touchPrimary ? { quality: 'medium' as const } : {}),
    ...loadSettings(),
  };

  function loadSettings(): Partial<GameSettings> {
    try {
      const raw = window.localStorage.getItem('life-empire:settings');
      return raw ? (JSON.parse(raw) as Partial<GameSettings>) : {};
    } catch {
      return {};
    }
  }

  function saveSettings(): void {
    try {
      window.localStorage.setItem('life-empire:settings', JSON.stringify(settings));
    } catch { /* storage may be unavailable; settings are not worth crashing for */ }
  }

  function applySettings(): void {
    audio.setMuted(settings.volume <= 0.001);
    audio.setVolume(settings.volume);
    const quality = settings.quality;
    renderer.shadowMap.enabled = quality !== 'low';
    pipeline.setQuality(quality);
    // A larger map on high, now that the filter is soft enough to use it.
    sky.sun.shadow.mapSize.setScalar(quality === 'high' ? 2048 : 1024);
    sky.sun.shadow.map?.dispose();
    sky.sun.shadow.map = null;
    crowd.setBudget(quality === 'high' ? 1 : quality === 'medium' ? 0.6 : 0.3);
    traffic.setBudget(quality === 'high' ? 1 : quality === 'medium' ? 0.6 : 0.25);
    ui.setHintsVisible(settings.showHints);
    saveSettings();
  }

  const ui = createUi(facade, {
    onRequestPointerLock: () => {
      if (!pointerLocked) renderer.domElement.requestPointerLock?.();
    },
    onReleasePointerLock: () => {
      if (pointerLocked) document.exitPointerLock?.();
    },
    onCelebrate: () => rig.addShake(0.55),
    sound: {
      ui: (kind) => audio.ui(kind),
      money: (kind) => audio.money(kind),
      milestone: () => audio.milestone(),
      toggleMute: () => {
        audio.setMuted(!audio.isMuted());
        return audio.isMuted();
      },
      isMuted: () => audio.isMuted(),
    },
  });
  // Under the HUD, over the world: see .le-vignette.
  const vignette = document.createElement('div');
  vignette.className = 'le-vignette';
  app.appendChild(vignette);

  app.appendChild(ui.root);
  ui.root.style.display = 'none';

  // A fade layer used for every transition, so nothing ever cuts to black.
  const fade = document.createElement('div');
  fade.className = 'le-fade';
  app.appendChild(fade);

  function fadeThrough(action: () => void): void {
    fade.classList.add('on', 'block');
    window.setTimeout(() => {
      action();
      window.setTimeout(() => fade.classList.remove('on', 'block'), 60);
    }, 460);
  }

  const title = createTitleScreen(APP_VERSION, {
    onNewGame: () => fadeThrough(() => startPlaying(true)),
    onContinue: (slot) => fadeThrough(() => {
      facade.load(slot);
      startPlaying(false);
    }),
    onLoad: (slot) => fadeThrough(() => {
      facade.load(slot);
      startPlaying(false);
    }),
    listSaves: () => {
      const found = new Map(facade.listSaves().map((s) => [s.slot, s]));
      return ['autosave', 'slot1', 'slot2', 'slot3']
        .map((slot) => describeSave(found.get(slot), slot));
    },
    getSettings: () => settings,
    setSettings: (next) => {
      Object.assign(settings, next);
      applySettings();
    },
    onQuit: isDesktop() ? () => window.close() : undefined,
    sound: { ui: (kind) => audio.ui(kind) },
  });
  app.appendChild(title.root);

  function startPlaying(fresh: boolean): void {
    if (fresh) facade.reset();
    mode = 'playing';
    title.hide();
    ui.root.style.display = '';
    touch?.setVisible(true);
    // A loaded save carries its own parked position; a fresh game has none and
    // falls back to the driveway. Doing this on every start rather than only
    // at boot is what stops a load leaving the car wherever the *previous*
    // save had put it.
    driver.exit();
    player.group.visible = true;
    driver.place(facade.vehicleParked() ?? DRIVEWAY);
    const spawn = getLocation('home');
    player.position.set(spawn.x + spawn.entryX + 5, 0, spawn.z + spawn.entryZ + 2);
    // Three-quarter opening framing. Looking straight at the porch means W
    // walks into a wall; looking straight down the street means the player
    // never sees the room they just woke up in. The offset gives both.
    rig.faceFrom(spawn.entryX, spawn.entryZ);
    rig.yaw += 2.1;
    rig.distance = 11;
    rig.pitch = 0.46;
    rig.snap();
    lastAutosaveDay = facade.sim.state.time.day;
    audio.resume();
  }

  function returnToTitle(): void {
    fadeThrough(() => {
      mode = 'title';
      title.show();
      ui.root.style.display = 'none';
      touch?.setVisible(false);
      pauseMenu.classList.remove('show');
      paused = false;
      if (pointerLocked) document.exitPointerLock?.();
    });
  }

  /**
   * An in-game pause menu rather than Escape dumping the player straight back
   * to the title. Escape is the universal "get me out of this" key and it must
   * not be able to lose someone's afternoon by accident.
   */
  let paused = false;
  const pauseMenu = document.createElement('div');
  pauseMenu.className = 'le-pause';
  app.appendChild(pauseMenu);

  function togglePause(): void {
    paused = !paused;
    pauseMenu.classList.toggle('show', paused);
    if (!paused) {
      pauseMenu.replaceChildren();
      return;
    }
    if (pointerLocked) document.exitPointerLock?.();
    audio.ui('open');

    const card = document.createElement('div');
    card.className = 'le-pause-card';
    const h = document.createElement('h2');
    h.textContent = 'Paused';
    const sub = document.createElement('div');
    sub.className = 'sub';
    const hud = facade.hud();
    sub.textContent = `${hud.dayName} ${hud.clock} · Day ${hud.day}`;
    card.append(h, sub);

    const item = (label: string, note: string, kind: string, action: () => void) => {
      const b = document.createElement('button');
      b.className = `le-menu-btn ${kind}`;
      const l = document.createElement('span');
      l.className = 'l';
      l.textContent = label;
      const s2 = document.createElement('span');
      s2.className = 's';
      s2.textContent = note;
      b.append(l, s2);
      b.onclick = () => { audio.ui('click'); action(); };
      return b;
    };

    card.append(
      item('Resume', 'Back to the game', 'primary', () => togglePause()),
      item('Save game', 'Write to slot 1', 'ghost', () => {
        facade.save('slot1');
        togglePause();
      }),
      item('Quit to title', 'Your progress is autosaved daily', 'ghost', () => {
        if (facade.sim.state.player.vehicleId) {
          const p = driver.parked();
          facade.setVehicleParked(p.x, p.z, p.heading);
        }
        facade.autosave();
        togglePause();
        returnToTitle();
      }),
    );
    pauseMenu.replaceChildren(card);
  }

  // --- touch ---------------------------------------------------------------
  // Nothing below runs on a desktop: `isTouchPrimary` requires a coarse
  // primary pointer, so a laptop with a touchscreen keeps its mouse controls.
  let touch: TouchControls | null = null;
  if (touchPrimary) {
    document.body.classList.add('le-touch');
    touch = createTouchControls(app, {
      look: (dx, dy) => rig.look(dx, dy),
      zoom: (delta) => rig.zoom(delta),
      interact: () => ui.interact(),
      phone: () => ui.handleKey('Tab'),
      pause: () => {
        if (ui.isModalOpen()) { ui.closeAll(); return; }
        togglePause();
      },
      isModalOpen: () => ui.isModalOpen() || paused,
      gesture: () => audio.resume(),
    });
    touch.setVisible(false);
  }

  /**
   * Get in or out of the car.
   *
   * Bound to F rather than E because E is the world interaction key and the
   * car is parked on the driveway, a few metres from the front door: sharing
   * one key there would mean the player could not reliably choose between
   * sleeping and driving.
   */
  function toggleDriving(): void {
    if (mode !== 'playing' || ui.isModalOpen() || paused) return;
    const owned = facade.sim.state.player.vehicleId;
    if (!vehicles.drivable(owned)) return;

    if (driver.driving) {
      const spot = driver.exitPoint();
      driver.exit();
      player.position.set(spot.x, 0, spot.z);
      player.group.visible = true;
      const p = driver.parked();
      facade.setVehicleParked(p.x, p.z, p.heading);
      audio.ui('close');
      return;
    }
    if (!driver.canEnter(player.position.x, player.position.z)) return;
    // Working and driving at once is not a state the job system understands:
    // the mower is on the lawn and the player is on the road.
    if (facade.sim.state.player.activeWork) facade.cancelWork();
    driver.enter();
    player.group.visible = false;
    audio.ui('open');
  }

  /** "Go inside" when standing at a door that leads somewhere, else null. */
  function doorLabel(): string | null {
    if (driver.driving) return null;
    const near = findNearestLocation(player.position.x, player.position.z);
    if (!near || !interiors.byLocation.has(near.id)) return null;
    const d = Math.hypot(
      player.position.x - (near.x + near.entryX),
      player.position.z - (near.z + near.entryZ),
    );
    return d <= 4.2 ? `Go inside · ${near.name}` : null;
  }

  /** The hotspot the player is standing on inside a room, if any. */
  function hotspotHere(): { kind: string; label: string } | null {
    if (!inside) return null;
    for (const spot of inside.hotspots) {
      const d = Math.hypot(player.position.x - spot.x, player.position.z - spot.z);
      if (d < spot.radius) return spot;
    }
    return null;
  }

  function enterInterior(def: InteriorDef): void {
    outsideReturn = { x: player.position.x, z: player.position.z };
    inside = def;
    interiors.setActive(def.locationId);
    player.position.set(def.spawn.x, 0, def.spawn.z);
    rig.faceFrom(Math.sin(def.spawn.facing), Math.cos(def.spawn.facing));
    rig.snap();
    audio.ui('open');
  }

  function leaveInterior(): void {
    if (!inside) return;
    inside = null;
    interiors.setActive(null);
    player.position.set(outsideReturn.x, 0, outsideReturn.z);
    rig.snap();
    audio.ui('close');
  }

  /**
   * E at a door, before the world interaction runs.
   *
   * Returns true when it handled the press. The exterior sleep and shop
   * interactions are deliberately left in place rather than replaced: they
   * are what the balance harness and the smoke tests drive, and making the
   * only route to a mower a walk through a room would put a 3D navigation
   * problem in front of every automated check of the economy.
   */
  function tryDoor(): boolean {
    if (mode !== 'playing' || ui.isModalOpen() || paused || driver.driving) return false;
    if (inside) {
      const spot = hotspotHere();
      if (spot?.kind === 'exit') { leaveInterior(); return true; }
      return false;
    }
    const near = findNearestLocation(player.position.x, player.position.z);
    if (!near) return false;
    const def = interiors.byLocation.get(near.id);
    if (!def) return false;
    // Only at the door itself. The interaction radius is generous enough that
    // sharing it would swallow the sleep prompt from halfway down the garden.
    const doorX = near.x + near.entryX;
    const doorZ = near.z + near.entryZ;
    const distance = Math.hypot(player.position.x - doorX, player.position.z - doorZ);
    if (distance > 4.2) return false;
    enterInterior(def);
    return true;
  }

  window.addEventListener('keydown', (e) => {
    // Never swallow typing in a text field.
    const target = e.target as HTMLElement | null;
    if (target && (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA')) return;

    audio.resume();
    if (mode === 'title') {
      title.handleKey(e.code);
      return;
    }
    keys.add(e.code);
    if (e.code === 'Escape' && !ui.isModalOpen()) {
      togglePause();
      e.preventDefault();
      return;
    }
    if (ui.handleKey(e.code)) {
      e.preventDefault();
      return;
    }
    if (e.code === 'KeyE') {
      if (!tryDoor()) ui.interact();
      e.preventDefault();
    }
    if (e.code === 'KeyF') {
      toggleDriving();
      e.preventDefault();
    }
  });
  window.addEventListener('keyup', (e) => keys.delete(e.code));
  window.addEventListener('blur', () => keys.clear());

  renderer.domElement.addEventListener('mousedown', () => {
    audio.resume();
    if (mode === 'title' || touchPrimary) return;
    if (!ui.isModalOpen()) renderer.domElement.requestPointerLock?.();
  });
  document.addEventListener('pointerlockchange', () => {
    pointerLocked = document.pointerLockElement === renderer.domElement;
    ui.setPointerLocked(pointerLocked);
  });
  window.addEventListener('mousemove', (e) => {
    if (pointerLocked) rig.look(e.movementX * settings.mouseSensitivity, e.movementY * settings.mouseSensitivity);
  });
  window.addEventListener(
    'wheel',
    (e) => {
      if (!ui.isModalOpen()) rig.zoom(e.deltaY);
    },
    { passive: true },
  );

  function resize(): void {
    camera.aspect = window.innerWidth / window.innerHeight;
    camera.updateProjectionMatrix();
    renderer.setSize(window.innerWidth, window.innerHeight);
    pipeline.setSize(window.innerWidth, window.innerHeight);
  }
  window.addEventListener('resize', resize);
  // Safari changes the viewport height as its toolbar collapses and expands
  // during play, and fires that on visualViewport rather than as a window
  // resize. Without this the canvas is left letterboxed after the first scroll
  // gesture. Rotating the device needs a beat before the new size is readable.
  window.visualViewport?.addEventListener('resize', resize);
  window.addEventListener('orientationchange', () => window.setTimeout(resize, 220));

  // --- loop ----------------------------------------------------------------
  const clock = new THREE.Clock();
  let lastAutosaveDay = 1;

  /**
   * Title mode: the simulation is frozen and the camera makes a slow circuit
   * over the neighbourhood at golden hour. The menu sits over a living world
   * rather than a still image, which is most of what makes a title screen feel
   * like a game rather than a launcher.
   */
  function stepTitle(dt: number): void {
    titleTime += dt;

    // High and slow, looking back across the green toward the far side of the
    // neighbourhood, so the shot reads as a place rather than as a rooftop.
    const radius = 96;
    const angle = titleTime * 0.035 + 0.6;
    const height = 40 + Math.sin(titleTime * 0.09) * 6;
    camera.position.set(
      Math.cos(angle) * radius,
      height,
      Math.sin(angle) * radius,
    );
    camera.lookAt(Math.cos(angle) * 8, 4, Math.sin(angle) * 8);
    camera.fov = 44;
    camera.updateProjectionMatrix();

    // A very slow golden-hour drift so the light is never static either.
    const hour = 17.6 + Math.sin(titleTime * 0.02) * 0.7;
    sky.setTimeOfDay(hour, scene, world.windowMaterial);
    refreshEnvironment(hour);
    followShadowCamera(sky, camera.position.x * 0.3, camera.position.z * 0.3);
    sky.update(dt);
    crowd.update(dt, facade.sim.world, hour, 0, 0);

    const lampTarget = Math.max(0, sky.nightFactor - 0.15) * 105;
    for (const light of world.lampLights) {
      light.intensity += (lampTarget - light.intensity) * Math.min(1, dt * 2.2);
    }
    world.lampMaterial.emissiveIntensity = 0.15 + sky.nightFactor * 1.1;

    for (const marker of world.jobMarkers.values()) marker.visible = false;
    signposts.update(9999, 9999, null, new Set());
    customers.update(dt, null, 9999, 9999);
    rain.update(dt, camera.position.x, camera.position.z);
    audio.updateAmbience(hour, sky.nightFactor);
  }

  /**
   * Who, if anyone, is waiting for the player right now.
   *
   * The job under way wins over a merely accepted lead: the person whose lawn
   * is being cut should be the one standing there watching it happen.
   */
  function expectingCustomer(): CustomerHost | null {
    const active = facade.sim.state.player.activeWork;
    if (active && active.kind === 'yard') {
      const lead = facade.leads().find((l) => l.id === active.refId);
      if (lead) {
        return { locationId: lead.locationId, name: lead.customerName, working: true };
      }
    }
    const booked = facade.leads().find((l) => l.accepted);
    if (!booked) return null;
    return { locationId: booked.locationId, name: booked.customerName, working: false };
  }

  function step(dt: number): void {
    if (mode === 'title') {
      stepTitle(dt);
      return;
    }
    // A paused game still draws, but nothing moves.
    if (paused) return;
    const hud = facade.hud();
    const blocked = ui.isModalOpen();
    const working = hud.activeWork !== null;

    // --- yard work -----------------------------------------------------------
    const activeLocation = facade.sim.state.player.activeWork?.locationId ?? null;
    if (activeLocation !== yardWorkLocation) {
      if (activeLocation) {
        // The simulation decides which parts of the job exist, from what the
        // player owns; the view only shows what is actually in the job.
        yardWork.begin(
          activeLocation,
          facade.workTasks().map((t) => t.kind),
        );
        const site = getLocation(activeLocation);
        rig.setWorkFocus({ x: site.x, z: site.z });
      } else {
        yardWork.end();
        rig.setWorkFocus(null);
      }
      yardWorkLocation = activeLocation;
    }
    // The player works the yard themselves: walking over uncut grass cuts it,
    // walking the boundary trims it, walking onto a heap clears it. Standing
    // still still burns the clock and leaves the job half done, which the
    // customer notices.
    let cutting = false;
    if (working) {
      const moving = Math.hypot(
        player.position.x - lastWorkX, player.position.z - lastWorkZ,
      ) > dt * 0.4;
      if (moving) cutting = yardWork.workAt(player.position.x, player.position.z);
      facade.setTaskProgress('mow', yardWork.coverage);
      facade.setTaskProgress('trim', yardWork.edgeProgress);
      facade.setTaskProgress('clear', yardWork.clearProgress);
      yardWork.update(dt, player.position, cutting);
    } else {
      yardWork.update(dt, null, false);
    }
    lastWorkX = player.position.x;
    lastWorkZ = player.position.z;

    // --- driving ----------------------------------------------------------
    // While driving, the car is what moves and the avatar rides along inside
    // it. Running both integrators would have the player walking around the
    // cabin, and the camera would not know which to follow.
    if (driver.driving) {
      const throttle = (keys.has('KeyW') ? 1 : 0) - (keys.has('KeyS') ? 1 : 0)
        + (touch?.state.forward ?? 0);
      const steer = (keys.has('KeyD') ? 1 : 0) - (keys.has('KeyA') ? 1 : 0)
        + (touch?.state.strafe ?? 0);
      const dry = facade.fuel().empty;
      driver.update(dt, {
        // An empty tank cuts the throttle but not the brakes or the steering,
        // so a car that runs dry rolls to a stop under control rather than
        // freezing mid-road.
        throttle: dry ? Math.min(0, clamp01(throttle)) : clamp01(throttle),
        steer: clamp01(steer),
        handbrake: keys.has('Space') || (touch?.state.running ?? false),
      }, world.colliders);

      const travelled = driver.takeDistance();
      if (travelled > 0) facade.drive(travelled);
      vehicles.animate(driver.state.speed, throttle < 0 && driver.state.speed > 0.5, dt);

      // The avatar rides in the driver's seat: hidden, but kept in step so
      // that getting out, the interaction radius and the save all agree about
      // where the player is.
      player.position.set(driver.state.x, 0, driver.state.z);
      player.group.position.copy(player.position);
    }

    player.update(
      dt,
      {
        // Keyboard and stick are summed rather than switched between, so a
        // device that has both keeps both, and clamped so holding W while
        // pushing the stick cannot ask for double speed.
        forward: driver.driving ? 0 : clamp01(
          (keys.has('KeyW') ? 1 : 0) - (keys.has('KeyS') ? 1 : 0) + (touch?.state.forward ?? 0),
        ),
        strafe: driver.driving ? 0 : clamp01(
          (keys.has('KeyD') ? 1 : 0) - (keys.has('KeyA') ? 1 : 0) + (touch?.state.strafe ?? 0),
        ),
        running: !driver.driving &&
          (keys.has('ShiftLeft') || keys.has('ShiftRight') || (touch?.state.running ?? false)),
        cameraYaw: rig.yaw,
      },
      blocked ? [] : (inside ? inside.colliders : world.colliders),
      inside?.bounds,
    );
    player.setWorking(working);

    // Inside a room the world's location lookup is meaningless - the player
    // is 600 m east of the neighbourhood. What stands in for it is the
    // hotspot they are standing on, which maps onto the same interaction the
    // exterior would have offered. Sleep is at the bed, the shop is at the
    // counter, and nowhere else in the room offers either.
    const spot = inside ? hotspotHere() : null;
    const near = inside
      ? (spot && spot.kind !== 'exit' ? getLocation(inside.locationId) : null)
      : findNearestLocation(player.position.x, player.position.z);
    facade.setPlayerPosition(
      player.position.x, player.position.z, player.facing, near?.id ?? null,
    );

    // The player now walks the property themselves, so leaving it has to mean
    // something. The margin is generous: stepping onto the pavement to get
    // round a hedge is not abandoning the job, walking down the street is.
    if (working) {
      const active = facade.sim.state.player.activeWork;
      if (active) {
        const site = getLocation(active.locationId);
        const away = Math.hypot(player.position.x - site.x, player.position.z - site.z);
        if (away > Math.max(site.width, site.depth) + 22) facade.cancelWork();
      }
    }

    facade.tick(dt);

    const ownedVehicle = facade.sim.state.player.vehicleId;
    if (ownedVehicle !== shownVehicle) {
      vehicles.setOwned(ownedVehicle);
      shownVehicle = ownedVehicle;
    }

    const kit = facade.sim.state.business.equipment;
    player.setCarrying(kit.length === 0 ? 'none' : kit.length >= 3 ? 'fullkit' : 'mower');

    // Yards with work waiting, used by both the beacons and the world labels.
    const active = facade.activeJobLocations();

    // Driving reframes the camera: further back and flatter, because at 16 m/s
    // the useful information is the road ahead rather than the figure.
    rig.setDriving(driver.driving);
    rig.setIndoors(inside !== null, inside?.cameraDistance);
    rig.update(
      dt,
      player.position,
      inside ? inside.colliders : world.colliders,
      driver.driving ? driver.speedFraction : player.speedFraction,
    );
    sky.setTimeOfDay(
      facade.sim.state.time.minuteOfDay / 60, scene, world.windowMaterial,
    );
    refreshEnvironment(facade.sim.state.time.minuteOfDay / 60);
    // The shadow camera covers a tight box around the player rather than the
    // whole neighbourhood, which is what buys sharp contact shadows.
    followShadowCamera(sky, player.position.x, player.position.z);
    sky.update(dt);
    crowd.update(
      dt, facade.sim.world, facade.sim.state.time.minuteOfDay / 60,
      player.position.x, player.position.z,
    );
    // Traffic keeps running while the player is indoors so the street is not
    // empty the moment they step back out, but it is fed the door they came
    // in by rather than their position 600 m away - otherwise every car would
    // retire on the distance check and the road would be bare.
    traffic.update(
      dt, facade.sim.state.time.minuteOfDay / 60,
      inside ? outsideReturn.x : player.position.x,
      inside ? outsideReturn.z : player.position.z,
      driver.driving ? driver.state : null,
    );

    // Audio follows what is actually happening on screen.
    audio.updateAmbience(facade.sim.state.time.minuteOfDay / 60, sky.nightFactor);
    audio.setMowerRunning(working);
    audio.setMowerCutting(yardWork.cutting);
    if (!working && player.speed > 0.6) {
      audio.footstep(keys.has('ShiftLeft') || keys.has('ShiftRight'));
    }

    for (const [id, marker] of world.jobMarkers) {
      marker.visible = active.has(id);
      if (marker.visible) {
        marker.rotation.y += dt * 1.4;
        marker.position.y = Math.sin(performance.now() / 480) * 0.45;
      }
    }

    // Street lamps come on as it gets dark, eased so the street does not blink.
    // Candela, not a 0..1 factor: at 26 m falloff a street lamp needs a
    // genuinely large value to put a pool of light on the pavement.
    const lampTarget = Math.max(0, sky.nightFactor - 0.15) * 105;
    for (const light of world.lampLights) {
      light.intensity += (lampTarget - light.intensity) * Math.min(1, dt * 2.2);
    }
    world.lampMaterial.emissiveIntensity =
      0.15 + sky.nightFactor * 1.1;

    if (hud.weather !== shownWeather) {
      shownWeather = hud.weather;
      sky.setWeather(hud.weather);
      rain.setIntensity(hud.weather === 'light_rain' ? 1 : 0);
      audio.setRain(hud.weather === 'light_rain' ? 1 : 0);
    }
    rain.update(dt, camera.position.x, camera.position.z);
    const wetTarget = hud.weather === 'light_rain' ? 1 : 0;
    // Slower to dry than to wet, the way a road behaves.
    wetness += (wetTarget - wetness) * Math.min(1, dt * (wetTarget > wetness ? 0.5 : 0.16));
    setGroundWetness(wetness);

    signposts.update(player.position.x, player.position.z, near?.id ?? null, active);
    customers.update(dt, expectingCustomer(), player.position.x, player.position.z);
    // The drive prompt is separate from the world interaction prompt: they can
    // both be relevant at once (the car is parked outside the front door), and
    // collapsing them into one would make the player guess which they got.
    ui.setDoorPrompt(inside ? (spot?.kind === 'exit' ? spot.label : null) : doorLabel());
    ui.setDrivePrompt(
      !inside && vehicles.drivable(facade.sim.state.player.vehicleId)
        ? (driver.driving
          ? 'exit'
          : (driver.canEnter(player.position.x, player.position.z) ? 'enter' : 'none'))
        : 'none',
      facade.fuel().fraction,
    );
    ui.update(near);
    if (touch) {
      touch.setModal(ui.isModalOpen() || paused);
      touch.setInteractEnabled(ui.hasInteraction());
    }

    if (facade.sim.state.time.day !== lastAutosaveDay) {
      lastAutosaveDay = facade.sim.state.time.day;
      if (facade.sim.state.player.vehicleId) {
        const p = driver.parked();
        facade.setVehicleParked(p.x, p.z, p.heading);
      }
      facade.autosave();
      // Lawns grow back, so a property you cut is worth cutting again later.
      yardWork.regrow();
    }
  }

  let running = true;

  function frame(): void {
    const dt = Math.min(clock.getDelta(), 0.1);
    if (running) {
      step(dt);
      pipeline.render();
    }
    requestAnimationFrame(frame);
  }

  // Draw one frame immediately so the first paint is the world behind the
  // title, not a blank page, then retire the pre-script boot overlay.
  await stage('Starting game');
  applySettings();
  stepTitle(0.016);
  pipeline.render();
  requestAnimationFrame(frame);
  finishBoot();

  window.__LIFE_EMPIRE__ = {
    facade,
    renderer,
    cameraRig: rig,
    scene,
    renderStats: pipeline.stats,
    /** Whether the post-processing stack is actually running. */
    composited: () => pipeline.composited,
    traffic,
    three: THREE,
    startGame: () => { title.hide(); startPlaying(true); },
    closeMenus: () => {
      ui.closeAll();
      paused = false;
      pauseMenu.classList.remove('show');
      pauseMenu.replaceChildren();
      fade.classList.remove('on', 'block');
    },
    showTitle: () => {
      mode = 'title'; title.show(); ui.root.style.display = 'none'; touch?.setVisible(false);
    },
    // Test and capture hook: put the avatar somewhere without simulating the
    // walk there. Used by the smoke test to mow a lawn deterministically.
    moveAvatarTo: (x: number, z: number) => { player.position.set(x, 0, z); },
    driving: {
      get active() { return driver.driving; },
      get state() { return driver.state; },
      toggle: () => toggleDriving(),
      place: (x: number, z: number, heading: number) => driver.place({ x, z, heading }),
    },
    interior: {
      get current() { return inside?.locationId ?? null; },
      get hotspot() { return hotspotHere()?.kind ?? null; },
      enter: (locationId: string) => {
        const def = interiors.byLocation.get(locationId);
        if (def) enterInterior(def);
      },
      leave: () => leaveInterior(),
      /** Walk straight to a hotspot; the test is of the room, not of pathing. */
      goTo: (kind: string) => {
        const spot = inside?.hotspots.find((h) => h.kind === kind);
        if (spot) player.position.set(spot.x, 0, spot.z);
      },
    },
    press: (code: string) => keys.add(code),
    release: (code: string) => keys.delete(code),
    setRunning: (value: boolean) => {
      running = value;
      // Reset the delta clock so resuming does not jump the world forward by
      // however long the loop was paused.
      clock.getDelta();
    },
    step: (seconds: number, render = true) => {
      step(seconds);
      if (render) pipeline.render();
    },
    ready: true,
  };
}

/**
 * Start, and never leave anyone looking at a bar that is not moving.
 *
 * Every failure path ends at a screen that says what broke and on which
 * device. The owner hit exactly this and got an animated progress bar
 * forever, because startup was one synchronous function with no catch and the
 * splash came down on its last line.
 */
registerServiceWorker();

void boot().catch((error: unknown) => {
  showFatalError(error, collectDiagnostics());
});

// A throw from module evaluation, or an unhandled rejection anywhere, never
// reaches the catch above.
window.addEventListener('error', (event) => {
  if (document.getElementById('le-boot') && !document.querySelector('.le-fatal')) {
    showFatalError(event.error ?? event.message, collectDiagnostics());
  }
});
window.addEventListener('unhandledrejection', (event) => {
  if (document.getElementById('le-boot') && !document.querySelector('.le-fatal')) {
    showFatalError(event.reason, collectDiagnostics());
  }
});

/** Clamp a summed movement axis back into the range the player rig expects. */
function clamp01(v: number): number {
  return v < -1 ? -1 : v > 1 ? 1 : v;
}
